/**
 * The four things a panel can be, written once.
 *
 * ## Why this file exists
 *
 * UI-20's finding: of the sixteen shared components that accept async data,
 * most owned an empty state, about half owned loading, four owned error, and
 * one owned permission-denied. Everything else rendered `null` or an empty
 * container — a silent blank rectangle, which to the person looking at it is
 * indistinguishable from a feature that was never built.
 *
 * Closing that meant adding roughly twenty state branches. Written out per
 * component that is twenty chances to spell a testid differently, twenty
 * chances to forget `role="alert"`, and twenty copies to keep in step — which
 * is the shape UI-13 and UI-16 both exist to undo, arriving in a new place.
 * So the branches are four components, used everywhere, and the a11y contract
 * is a property of the implementation rather than of everybody's memory.
 *
 * It is also the cheapest option by weight: the initial bundle has a hard
 * ceiling that may only go down, and one implementation reused sixteen times
 * is a few hundred bytes where sixteen hand-written ones are several KB.
 *
 * ## The contract (plan §B.4, non-negotiable, and enforced by the sweep)
 *
 * | State | Contract |
 * |---|---|
 * | loading | container `aria-busy="true"`; skeletons `aria-hidden="true"`; testid `<prefix>-loading` |
 * | error | `role="alert"`; plain-English message naming what failed; a retry control ONLY if retrying can help; testid `<prefix>-error` |
 * | denied | `role="status"`; explains access; NO retry; testid `<prefix>-denied` |
 * | empty | no role; testid `<prefix>-empty`; copy distinguishes "nothing here yet" from "nothing matched your filter" |
 *
 * `role="status"` on denied rather than `alert` is deliberate and it is the
 * one place the contract corrects what shipped. A closed door is not an
 * emergency; announcing it assertively interrupts a screen-reader user for
 * something that is not going to change. `alert` is for the thing that broke.
 *
 * Retry is a prop, not a default, for the same reason RelatedTab's
 * permission-denied branch has never had one: a button that cannot work is
 * what makes a closed door look like a broken screen.
 */

import { type ReactElement, type ReactNode } from "react";

import { cn } from "../lib/utils";
import { Button } from "./button";
import { Skeleton } from "./skeleton";

/** The four states this file renders. Also the vocabulary the sweep counts. */
export type AsyncStateName = "loading" | "error" | "empty" | "denied";

export interface AsyncStateProps {
  /**
   * Testid namespace. The emitted id is `<idPrefix>-<state>`, e.g.
   * `activity-timeline-error`. Components that already ship a different id
   * pass it explicitly through `testId` — see `ASYNC_STATE_CONTRACT`, where
   * every such exception is written down with its reason.
   */
  idPrefix: string;
  /** Overrides the derived `<idPrefix>-<state>` id. */
  testId?: string;
  className?: string;
}

// ---------------------------------------------------------------------------
// loading
// ---------------------------------------------------------------------------

export interface StateLoadingProps extends AsyncStateProps {
  /** How many skeleton bars. Default 3. */
  rows?: number;
  /** What is being loaded, for the screen-reader label: "Loading {label}…". */
  label?: string;
}

export function StateLoading({
  idPrefix,
  testId,
  rows = 3,
  label,
  className,
}: StateLoadingProps): ReactElement {
  return (
    <div
      data-testid={testId ?? `${idPrefix}-loading`}
      aria-busy="true"
      aria-label={label ? `Loading ${label}` : undefined}
      className={cn("flex flex-col gap-2 py-4", className)}
    >
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton
          key={i}
          aria-hidden="true"
          className={i === 0 ? "h-4 w-40" : "h-4 w-full"}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// error
// ---------------------------------------------------------------------------

export interface StateErrorProps extends AsyncStateProps {
  /**
   * What could not be loaded, in the reader's words — "the activity history",
   * "this record's notes". Rendered as "Couldn't load {what}."
   */
  what: string;
  /**
   * Offered as Retry when supplied. Omit it when retrying cannot help: a
   * button that cannot work is what makes a closed door look like a broken
   * screen.
   */
  onRetry?: (() => void) | undefined;
  /** Overrides the derived `<idPrefix>-error-retry` id on the retry control. */
  retryTestId?: string;
}

export function StateError({
  idPrefix,
  testId,
  what,
  onRetry,
  retryTestId,
  className,
}: StateErrorProps): ReactElement {
  return (
    <div
      data-testid={testId ?? `${idPrefix}-error`}
      role="alert"
      className={cn("flex flex-col items-start gap-3 py-4", className)}
    >
      <p className="text-sm text-[var(--color-status-overdue-text)]">
        Couldn&apos;t load {what}.
      </p>
      {onRetry && (
        <Button
          variant="solid"
          size="sm"
          data-testid={retryTestId ?? `${idPrefix}-error-retry`}
          onClick={onRetry}
        >
          Retry
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// permission denied
// ---------------------------------------------------------------------------

export interface StateDeniedProps extends AsyncStateProps {
  /** What the viewer may not see — "this record's connections". */
  what: string;
}

export function StateDenied({
  idPrefix,
  testId,
  what,
  className,
}: StateDeniedProps): ReactElement {
  return (
    <div
      data-testid={testId ?? `${idPrefix}-denied`}
      role="status"
      className={cn("flex flex-col items-start gap-2 py-4", className)}
    >
      <p className="text-sm text-[var(--color-text-strong)]">
        You don&apos;t have access to {what}.
      </p>
      <p className="text-sm text-[var(--color-text-soft)]">
        Ask an administrator if you need to see this.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// empty
// ---------------------------------------------------------------------------

export interface StateEmptyProps extends AsyncStateProps {
  /**
   * The copy. It must distinguish "nothing here yet" from "nothing matched
   * your filter" — the two mean opposite things to somebody deciding whether
   * to widen a search, and one sentence covering both tells them neither.
   */
  children: ReactNode;
}

export function StateEmpty({
  idPrefix,
  testId,
  children,
  className,
}: StateEmptyProps): ReactElement {
  return (
    <div
      data-testid={testId ?? `${idPrefix}-empty`}
      className={cn(
        "py-8 text-center text-sm text-[var(--color-text-soft)]",
        className,
      )}
    >
      {children}
    </div>
  );
}
